import React, { useRef, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import jwt_decode from "jwt-decode";
import Backdrop from "../Backdrop/Backdrop";
import CircularProgress from "../CircularProgress/CircularProgress";
import SingleActionModal from "../SingleActionModal/SingleActionModal";
import useSessionCredential from "../../utils/useSessionCredential";
import useSilentTokenRefresh, {
  isNearExpiry,
} from "../../utils/useSilentTokenRefresh";
import useUserInfo from "../../utils/useUserInfo";
import { clearAuthetication } from "../../utils/userData";
import "../GoogleLogin/Login.css";

export default function Login({ dispatchUserInfo }) {
  const [credential, saveCredential] = useSessionCredential();
  const { refreshTick, clearRefresh } = useSilentTokenRefresh(credential);
  const { userInfo, isLoading } = useUserInfo(credential, dispatchUserInfo);
  const [refreshBlocked, setRefreshBlocked] = useState(false);
  const [renewDismissed, setRenewDismissed] = useState(false);

  // GIS captures the moment callback when prompt() fires, so it can be
  // invoked long after later renders — e.g. the user closes a leftover
  // prompt AFTER the refresh already succeeded. These refs let the callback
  // see the current session instead of the stale closure it was born with.
  const credentialRef = useRef(credential);
  credentialRef.current = credential;
  const renewDismissedRef = useRef(renewDismissed);
  renewDismissedRef.current = renewDismissed;

  const onSuccess = (res) => {
    saveCredential(res.credential);
    clearRefresh();
    setRefreshBlocked(false);
    setRenewDismissed(false);
  };

  const onFailure = (res) => {
    console.log("Login failed: res:", res);
    clearAuthetication();
  };

  // A failed silent refresh keeps the current session: the next tab focus
  // retries, and if the token fully expires App shows the sign-in modal
  const onRefreshError = (res) => {
    console.log("Silent refresh failed: ", res);
  };

  // The browser can block FedCM prompts entirely (past dismissals, site
  // settings) and a skipped moment is the only signal we get. Fall back to
  // a real sign-in button: its popup flow does not depend on FedCM.
  // Only a token still near expiry needs that rescue — a skipped moment
  // from a leftover prompt dismissed after a successful refresh must NOT
  // flash the renew modal over a healthy session.
  const onRefreshMoment = (notification) => {
    if (!notification.isSkippedMoment() || renewDismissedRef.current) return;
    const currentCredential = credentialRef.current;
    if (!currentCredential || !isNearExpiry(currentCredential)) return;
    setRefreshBlocked(true);
  };

  const dismissRenew = () => {
    setRefreshBlocked(false);
    setRenewDismissed(true);
  };

  if (credential) {
    const currentEmail = jwt_decode(credential.id_token).email;
    return (
      <div>
        {/* Boot with a stored credential: block on the spinner only until
            the first userInfo arrives — refetches after a silent refresh
            must NOT flash this over a working session */}
        <Backdrop show={isLoading && !userInfo}>
          <CircularProgress />
        </Backdrop>
        <span hidden key={userInfo?.email} id="email">
          {userInfo?.email}
        </span>
        <span hidden key={credential.id_token} id="id_token">
          {credential.id_token}
        </span>
        <span hidden id="impersonated_user"></span>
        {refreshTick > 0 && !refreshBlocked && (
          <span hidden>
            {/* login_hint pins One Tap to the signed-in account so the
                refresh never shows the account chooser */}
            <GoogleLogin
              key={refreshTick}
              onSuccess={onSuccess}
              onError={onRefreshError}
              promptMomentNotification={onRefreshMoment}
              useOneTap
              auto_select
              login_hint={currentEmail}
              cancel_on_tap_outside={false}
            />
          </span>
        )}
        <Backdrop show={refreshBlocked}>
          <SingleActionModal
            headerImgUrl={"/athena-logo.png"}
            title={"Your session is about to expire"}
            content={
              "Your work is saved. Sign in again to pick up right where you left off."
            }
          >
            {/* Deliberately no GIS button here: since FedCM became mandatory
                (Aug 2025) it can fail to render exactly when this modal
                appears (third-party sign-in blocked). Sign-out + reload is
                the reliable path — the login screen's button renders fresh
                on page load and its popup flow works even while blocked. */}
            <div className="d-flex flex-column align-items-center w-100">
              <button
                type="button"
                className="btn btn-primary fw-bold px-4 custom-btn modal-text"
                onClick={() => clearAuthetication(true)}
              >
                Sign in again
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm mt-2"
                onClick={dismissRenew}
              >
                Dismiss
              </button>
            </div>
          </SingleActionModal>
        </Backdrop>
      </div>
    );
  }

  return (
    <div>
      <Backdrop show={isLoading}>
        <CircularProgress />
      </Backdrop>
      <div className="title">ATHENA LAUNCHPAD</div>
      <div className="center">
        {/* cancel_on_tap_outside keeps stray clicks from dismissing One Tap —
            each dismissal makes the browser suppress the prompt for hours */}
        <GoogleLogin
          onSuccess={onSuccess}
          onError={onFailure}
          useOneTap
          auto_select
          cancel_on_tap_outside={false}
        />
      </div>
    </div>
  );
}
